import { Template } from 'src/api/template';
import dayjs from 'dayjs';

interface SingleTemplateProps {
  item: Template;
  navigate: (path: string, options?: any) => void;
  publishingId: string | null;
  handlePublish: (templateId: string) => void;
  openPopoverId: string | null;
  setOpenPopoverId: (id: string | null) => void;
  subjects: any[];
}

const SingleTemplate = ({
  item,
  navigate,
  publishingId,
  handlePublish,
  openPopoverId,
  setOpenPopoverId,
  subjects,
}: SingleTemplateProps) => {
  const statusColor =
    item.status === 'published' ? 'bg-green-600' : 'bg-red-600';

  const subject = subjects.find(sub => sub.id === item.subject)?.name;

  const isPublishing = publishingId === item._id;

  return (
    <div
      key={item._id}
      className="w-fit sm:w-1/2 md:w-1/3 lg:w-1/4 xl:w-1/4 mb-6 cursor-pointer rounded-[12px] border bg-white h-full relative"
      onClick={() =>
        navigate(`/templates/${item._id}/details`, { state: { item } })
      }
    >
      <div className="w-full h-full rounded-[12px] shadow-md border border-[#E4E7EC] relative bg-white flex flex-col items-start gap-1 p-1">
        <div className="relative mb-1 w-full">
          <img
            src={item.thumbnail || '/placeholder.jpg'}
            alt="Preview"
            className="w-full h-32 object-cover aspect-[4/3]"
          />
          {typeof item.status === 'string' && (
            <button
              className={`absolute top-2 right-2 ${statusColor} text-white px-3 py-1 rounded text-sm shadow`}
            >
              {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
            </button>
          )}
        </div>

        <div className="rounded-[8px] bg-[#F2F4F7] flex flex-col items-start gap-[6px] self-stretch px-[12px] py-[8px]">
          <div className="flex justify-between items-center w-full">
            <h3 className="font-semibold text-gray-800 text-base text-[18px]">
              {item.name || 'Untitled Template'}
            </h3>
            {item.status === 'draft' && (
              <div className="relative">
                <button
                  className="p-1 rounded-full hover:bg-gray-200 focus:outline-none"
                  onClick={e => {
                    e.stopPropagation(); // Prevent card click
                    setOpenPopoverId(
                      openPopoverId === item._id ? null : item._id
                    );
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-5 h-5 text-gray-500"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M6.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM18.75 12a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0Z"
                    />
                  </svg>
                </button>
                {openPopoverId === item._id && (
                  <div
                    className="absolute right-0 mt-2 w-40 bg-white border border-gray-200 rounded-md shadow-lg z-10 transition-opacity duration-200 ease-out opacity-100"
                    onClick={e => e.stopPropagation()}
                  >
                    <button
                      onClick={() => handlePublish(item._id)}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
                      disabled={isPublishing}
                    >
                      Publish
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex justify-between items-center gap-[12px] self-stretch text-[12px]">
            <span className="text-gray-500">{subject}</span>
            <span className="text-gray-400">
              {dayjs(item.created_at).format('MMM D,YYYY')}
            </span>
          </div>
        </div>
      </div>
      {isPublishing && (
        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-[12px] z-20">
          <div className="flex flex-col items-center text-white">
            <svg
              className="animate-spin h-8 w-8 text-white"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <p className="mt-2 text-sm">Publishing...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default SingleTemplate;
